import { useCallback, useMemo } from 'react';

import { useLoggedInUserRoles } from '~/components/Auth';
import {
  addCustomDataPage,
  addCustomNavButtonSettings,
  removeCustomDataPage,
  removeNavButtonSettings,
  removeRewardingSectionCustomData,
} from '~/components/Forms/helper';
import { useApplicationConfig } from '~/data/applicationConfig';
import { useModulesConfig } from '~/data/company';
import { useAllEnums } from '~/data/enums';
import { isRoleHRAdmin } from '~/services/Permissions';
import { FormioComponent, FormioFormBuilderConfig } from '~/types/forms';
import { browserLogError } from '~/utils/helper';

import { useCustomPageComponents } from './useCustomPageComponents';

export const useFormsCustomConfig = (formId: string, isBuilder = false) => {
  const roles = useLoggedInUserRoles();
  const { applicationConfig, isLoading: isLoadingApplicationConfig } =
    useApplicationConfig();

  const { is7DaysEnabled } = useMemo(() => {
    const is7DaysEnabled =
      applicationConfig?.fComConfig?.absConfig?.fComEnable7DaysVacationRule ??
      false;

    const vacationCalculationDayOptions = is7DaysEnabled
      ? ['5', '6', '7']
      : ['5', '6'];

    return {
      is7DaysEnabled,
      vacationCalculationDayOptions,
    };
  }, [applicationConfig]);

  const { getCustomDataEnums } = useAllEnums();
  const { enabledModules, isLoading: isLoadingModulesConfig } =
    useModulesConfig();
  const customDataEnabled = enabledModules.includes('CUSTOM_DATA');

  const isHRAdmin = isRoleHRAdmin(roles);

  const personnelGroupNames = useMemo(
    () =>
      getCustomDataEnums('PERSONNEL_GROUP_NAME')?.reduce((acc, obj) => {
        return { ...acc, [obj.code]: obj.name };
      }, {} as Record<string, string>),
    [getCustomDataEnums],
  );
  const personnelGroupNamesLoaded = Boolean(
    personnelGroupNames && Object.keys(personnelGroupNames).length > 0,
  );

  const employeeCustomDataEnums = useMemo(() => {
    return getCustomDataEnums('CUSTOM_ENTITY_EMPLOYEE') || [];
  }, [getCustomDataEnums]);

  const { data: apiResp, isLoading: isLoadingCustomComponents } =
    useCustomPageComponents(
      undefined,
      !(!!formId && !!employeeCustomDataEnums.length),
    );
  const customDataComponents = useMemo(() => {
    if (apiResp && employeeCustomDataEnums.length) {
      return employeeCustomDataEnums.map((item) => ({
        ...apiResp[item.code]?.schema,
        disabled: item.editableByEmp && !isHRAdmin && !isBuilder,
      }));
    }
    return [];
  }, [apiResp, employeeCustomDataEnums, isHRAdmin, isBuilder]);

  const isLoading =
    isLoadingApplicationConfig ||
    isLoadingCustomComponents ||
    isLoadingModulesConfig;

  // Memoize so the returned reference is stable across renders. Consumers
  // (useFormsRehire, useFormsOverlappingEmp) put applyCustomConfig in useMemo
  // dep arrays for formConfigData — an unstable function reference busted
  // the memoization and rebuilt the Formio form on every parent re-render.
  const applyCustomConfig = useCallback(
    (formConfig: FormioFormBuilderConfig): FormioFormBuilderConfig => {
      try {
        const pageIndex =
          formConfig?.components?.findIndex((comp) => comp.key === 'page4') ??
          -1;
        const page = (formConfig?.components ?? [])[pageIndex ?? -1];

        // implements seven days vacations rule
        if (!is7DaysEnabled && page) {
          const componentIndex =
            page?.components?.findIndex(
              (comp: FormioComponent) =>
                comp.key === 'vacationBalanceCalculation',
            ) ?? -1;
          const component = (page?.components || [])[componentIndex ?? -1];
          // @ts-ignore
          formConfig.components[pageIndex].components[componentIndex].values =
            component?.values?.slice(0, 2);
        }

        // implements personnel group names dynamic translation
        if (personnelGroupNamesLoaded && page) {
          const personnelGroupsIndex =
            page?.components?.findIndex(
              (comp: FormioComponent) => comp.key === 'personnelGroups',
            ) ?? -1;
          const personnelGroupsFieldset = (page?.components ?? [])[
            personnelGroupsIndex
          ];
          // @ts-ignore
          formConfig.components[pageIndex].components[personnelGroupsIndex] = {
            ...personnelGroupsFieldset,
            components:
              personnelGroupsFieldset?.components?.map((comp, idx) => {
                return {
                  ...comp,
                  label: personnelGroupNames[`PERSONNEL_GROUP_${idx + 1}`],
                  placeholder:
                    personnelGroupNames[`PERSONNEL_GROUP_${idx + 1}`],
                };
              }) ?? [],
          };
        }

        const formConfigWithoutFooterNavButton =
          removeNavButtonSettings(formConfig);
        if (customDataEnabled) {
          addCustomDataPage(
            formConfigWithoutFooterNavButton,
            customDataComponents,
          );
        } else {
          removeCustomDataPage(formConfigWithoutFooterNavButton);
          removeRewardingSectionCustomData(formConfig);
        }

        if (!isBuilder) {
          return addCustomNavButtonSettings(
            formConfig,
          ) as FormioFormBuilderConfig;
        }

        return {};
      } catch (error) {
        browserLogError('Error applying custom form config:', error);
        return formConfig;
      }
    },
    [
      is7DaysEnabled,
      personnelGroupNames,
      personnelGroupNamesLoaded,
      customDataEnabled,
      customDataComponents,
      isBuilder,
    ],
  );

  return { applyCustomConfig, customDataComponents, isLoading };
};
